// ============================================================================
// AXIOS INTERCEPTORS
// ============================================================================
// Two sets of interceptors:
// - App client: Bearer token, tenant headers, proactive + reactive refresh
// - Auth client: Only tenant context headers (no Bearer token)

import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiConfig, HTTP_STATUS } from '../config/apiConfig';
import { tokenManager } from '../../auth/tokenManager';
import { STORAGE_KEYS } from '../../auth/storageKeys';
import { logRequest, logResponse, logError, logWarning } from './logger';

const TOKEN_REFRESH_BUFFER_MS = 60_000; // 60 seconds before expiry

// ============================================================================
// APP CLIENT INTERCEPTORS (authenticated business endpoints)
// ============================================================================

export const setupAppInterceptors = (axiosInstance: AxiosInstance): void => {
  // === Request Interceptor ===
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      config.headers['X-Request-Start-Time'] = Date.now().toString();

      // Proactive token refresh: if token expires within 60s, refresh BEFORE sending
      const token = tokenManager.getAccessToken();
      if (token) {
        const timeUntilExpiry = tokenManager.getTimeUntilExpiry(token);
        if (timeUntilExpiry !== null && timeUntilExpiry < TOKEN_REFRESH_BUFFER_MS && timeUntilExpiry > 0) {
          try {
            await proactiveRefresh(axiosInstance);
          } catch {
            // If proactive refresh fails, continue with current token
            // The response interceptor will handle 401 reactively
          }
        }
        // Attach token (possibly refreshed)
        const currentToken = tokenManager.getAccessToken();
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
      }

      // Attach tenant context headers
      const tenantId = tokenManager.getTenantId();
      const appId = tokenManager.getAppId();
      const userId = tokenManager.getUserId();
      const fyId = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_ID);
      const entityId = localStorage.getItem(STORAGE_KEYS.SELECTED_ENTITY_ID);
      const buId = localStorage.getItem(STORAGE_KEYS.SELECTED_BU_ID);

      if (tenantId) config.headers['X-Tenant-ID'] = tenantId;
      if (appId) config.headers['X-App-ID'] = appId;
      if (userId) config.headers['X-User-ID'] = userId;
      if (fyId) config.headers['X-Financial-Year-ID'] = fyId;
      if (entityId) config.headers['X-Entity-ID'] = entityId;
      if (buId) config.headers['X-Business-Unit-ID'] = buId;

      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      config.headers['Accept'] = 'application/json';

      logRequest(config);
      return config;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );

  // === Response Interceptor (reactive 401 handling) ===
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      logResponse(response);
      return response;
    },
    async (error: AxiosError) => {
      logError(error);

      if (error.response) {
        const { status } = error.response;

        if (status === HTTP_STATUS.UNAUTHORIZED) {
          return handleUnauthorizedError(axiosInstance, error);
        }

        if (status === HTTP_STATUS.FORBIDDEN) {
          logWarning('Access forbidden - Insufficient permissions');
        }

        if (status === HTTP_STATUS.NOT_FOUND) {
          logWarning('Resource not found', error.config?.url);
        }

        if (status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
          logWarning('Internal server error');
        }
      } else if (error.request) {
        logWarning('Network error - No response from server');
      } else {
        logWarning('Unknown error occurred', error.message);
      }

      return Promise.reject(error);
    }
  );
};

// ============================================================================
// AUTH CLIENT INTERCEPTORS (discover, authenticate, refresh — no Bearer token)
// ============================================================================

export const setupAuthInterceptors = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers['X-Request-Start-Time'] = Date.now().toString();

      // Only attach tenant context (no Bearer token for auth endpoints)
      const tenantId = tokenManager.getTenantId();
      // The login flow (discover/authenticate/refresh) targets the app
      // configured at build time. Prefer REACT_APP_APPLICATION_ID over any
      // stored app id so a stale value from a previous session (different
      // user/app) cannot hijack discover — only fall back to the stored id
      // if the env var is absent.
      const appId = process.env.REACT_APP_APPLICATION_ID || tokenManager.getAppId();

      if (tenantId) config.headers['X-Tenant-ID'] = tenantId;
      if (appId) config.headers['X-App-ID'] = appId;

      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      config.headers['Accept'] = 'application/json';

      logRequest(config);
      return config;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      logResponse(response);
      return response;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );
};

// ============================================================================
// REACTIVE TOKEN REFRESH (401 handler with request queue)
// ============================================================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

async function handleUnauthorizedError(
  axiosInstance: AxiosInstance,
  error: AxiosError
): Promise<any> {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  if (originalRequest._retry) {
    logWarning('Token refresh failed, redirecting to login');
    tokenManager.clearAll();
    window.location.href = '/';
    return Promise.reject(error);
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      })
      .catch(err => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  const refreshToken = tokenManager.getRefreshToken();

  if (!refreshToken) {
    logWarning('No refresh token available, redirecting to login');
    tokenManager.clearAll();
    window.location.href = '/';
    return Promise.reject(error);
  }

  try {
    const newToken = await performTokenRefresh(axiosInstance);
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    processQueue(null, newToken);
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError, null);
    tokenManager.clearAll();
    window.location.href = '/';
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

// ============================================================================
// PROACTIVE TOKEN REFRESH
// ============================================================================

let isProactiveRefreshing = false;

async function proactiveRefresh(axiosInstance: AxiosInstance): Promise<void> {
  if (isProactiveRefreshing || isRefreshing) return;
  isProactiveRefreshing = true;
  try {
    await performTokenRefresh(axiosInstance);
  } finally {
    isProactiveRefreshing = false;
  }
}

// ============================================================================
// SHARED REFRESH LOGIC
// ============================================================================

async function performTokenRefresh(_axiosInstance: AxiosInstance): Promise<string> {
  // Use a separate axios call to avoid interceptor loops (otherwise the
  // expired-token attached by the request interceptor would re-trigger
  // the 401 → refresh cycle indefinitely).
  const { default: axios } = await import('axios');
  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) {
    // Nothing to refresh with — caller's reactive-401 path will clear
    // tokens and redirect to login.
    throw new Error('NO_REFRESH_TOKEN');
  }
  const tenantId = tokenManager.getTenantId();
  const appId = tokenManager.getAppId();

  // Endpoint selection based on auth mode:
  //   • tenant-hub (default, used in dev/UAT/prod against the real
  //     TenantHub backend) → POST /auth/refresh-hub, which the PRIMA API
  //     proxies to TenantHub's /Auth/refresh. The local /auth/refresh
  //     endpoint is [Authorize]-protected and only emits hardcoded
  //     dev-credentials JWTs — useless for a real Keycloak session.
  //   • legacy (REACT_APP_AUTH_MODE=legacy, only the mock-credentials
  //     dev mode) → POST /auth/refresh, regenerates a local dev JWT.
  const isTenantHub = process.env.REACT_APP_AUTH_MODE !== 'legacy';
  const path = isTenantHub ? '/auth/refresh-hub' : '/auth/refresh';

  const response = await axios.post(
    `${apiConfig.baseURL}/${apiConfig.apiVersion}${path}`,
    {
      refreshToken,
      tenantId,
      applicationId: appId,
    },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: apiConfig.timeout,
    }
  );

  const data = response.data;
  // Response-shape tolerance: TenantHub typically wraps in
  // { success, data: { accessToken, refreshToken, ... } }; the legacy
  // local-dev endpoint returns the token fields at the root with the
  // alias `token`. Accept both transparently.
  const tokenData = data.data ?? data;
  const newAccessToken = tokenData.accessToken ?? tokenData.token;
  const newRefreshToken = tokenData.refreshToken;

  if (!newAccessToken) {
    // The refresh endpoint responded 200 but the body didn't carry a
    // usable token. Treat as a refresh failure so the reactive path
    // clears state and redirects to login (rather than retrying the
    // original request with an empty Bearer header).
    throw new Error('REFRESH_RESPONSE_MISSING_TOKEN');
  }

  tokenManager.setAccessToken(newAccessToken);
  if (newRefreshToken) {
    // TenantHub rotates the refresh token on every refresh (security
    // best practice — invalidates the previous one). Always overwrite
    // when the response carries one.
    tokenManager.setRefreshToken(newRefreshToken);
  }

  return newAccessToken;
}
